import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import {
  CapabilityUser,
  ProfileLike,
  toCapabilityUser,
} from './capability-user.adapter';
import { CapabilityName } from './registry-ot.generated';
import { checkCapability } from './resolver';

/**
 * The Angular equivalent of FGR's `useCapabilities`. A PASSIVE holder: it caches
 * the latest `CapabilityUser` and answers capability questions over it. It does
 * NOT inject `ProfileService` — `ProfileService` pushes the profile in via
 * `setProfile()` (PR2). This keeps the runtime dependency one-way
 * (`core -> capabilities`) and avoids a circular DI / module dependency.
 *
 * Until `setProfile()` is wired (PR1 ships the lib unconsumed), every check
 * resolves against `null` -> `false`, which is the correct "no profile yet" state.
 */
@Injectable({ providedIn: 'root' })
export class CapabilitiesService {
  private readonly _user$ = new BehaviorSubject<CapabilityUser | null>(null);

  /** Feed the current profile (normalized through the adapter). Call on every profile change. */
  setProfile(profile: ProfileLike | null | undefined): void {
    this._user$.next(toCapabilityUser(profile));
  }

  /** Sync check against the latest profile — for guards and TS logic (e.g. deal-matrix). */
  check(name: CapabilityName): boolean {
    return checkCapability(name, this._user$.value);
  }

  /** Reactive check for templates: `*otCapability` / `caps.check$('canX') | async`. */
  check$(name: CapabilityName): Observable<boolean> {
    return this._user$.pipe(
      map((user) => checkCapability(name, user)),
      distinctUntilChanged()
    );
  }
}
